import { useCallback, useMemo, useState, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Avatar, Box, Button, Fab, FormHelperText, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import getCroppedImg from "./Crop";
import { AGENT_IMAGES_URL } from "../../constants/url";

const initialCrop = { x: 0, y: 0 };

const AgentImage = () => {
  const [crop, setCrop] = useState(initialCrop);
  const [zoom, setZoom] = useState(1);
  const [image, setImage] = useState(null); // objectURL for modal
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { setValue, control, getValues, trigger } = useFormContext();

  const agentimageUrl  = useWatch({ control, name: "agentimage" });      // existing URL (string)
  const agentimageFile = useWatch({ control, name: "agentimageFile" });  // new Blob from crop

  // Build preview URL for the Avatar
  const previewSrc = useMemo(() => {
    if (agentimageFile instanceof Blob) {
      return URL.createObjectURL(agentimageFile);
    }
    if (typeof agentimageUrl === "string" && agentimageUrl.length > 0) {
      return AGENT_IMAGES_URL + agentimageUrl;
    }
    return "";
  }, [agentimageFile, agentimageUrl]);

  // Cleanup generated preview objectUrls
  useEffect(() => {
    return () => {
      if (agentimageFile instanceof Blob) {
        try { URL.revokeObjectURL(previewSrc); } catch {}
      }
    };
  }, [agentimageFile, previewSrc]);

  const handleImageUpload = (e) => {
    setZoom(1);
    const file = e.target.files?.[0];
    if (file) setImage(URL.createObjectURL(file));
  };

  const onCropComplete = useCallback((_c, pixels) => setCroppedAreaPixels(pixels), []);

  const onApplyCrop = useCallback(async () => {
    if (!image || !croppedAreaPixels) return;
    setIsLoading(true);
    try {
      const croppedBlob = await getCroppedImg(image, croppedAreaPixels); // -> Blob
      setValue("agentimageFile", croppedBlob, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      // revalidate the "agentimage" field since our rule checks either/or
      await trigger("agentimage");

      URL.revokeObjectURL(image);
      setImage(null);
    } finally {
      setIsLoading(false);
    }
  }, [image, croppedAreaPixels, setValue, trigger]);

  const onClose = () => {
    if (image) URL.revokeObjectURL(image);
    setImage(null);
  };

  return (
    <div>
      <Controller
        control={control}
        name="agentimage" // server URL string if editing; may be ""
        rules={{
          validate: () => {
            const hasUrl  = !!getValues("agentimage");
            const hasFile = getValues("agentimageFile") instanceof Blob;
            return hasUrl || hasFile || "Staff Image is Required";
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <input
              id="agent-image-input"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              hidden
            />
            <Avatar
              variant="rounded"
              src={previewSrc || undefined}
              component="label"
              htmlFor="agent-image-input"
              sx={{ width: 100, height: 100, cursor: "pointer" }}
            />
            <FormHelperText error sx={{ textAlign: "center", maxWidth: 100, m: 0 }}>
              {error?.message}
            </FormHelperText>
          </>
        )}
      />

      <Modal open={!!image} onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ height: "100%", width: "100%", maxWidth: { xs: 360, sm: 500 }, maxHeight: { xs: 360, sm: 500 }, position: "relative", p: 2 }}>
          <Cropper
            style={{ containerStyle: { width: "100%", height: "100%" } }}
            image={image}
            crop={crop}
            zoom={zoom}
            objectFit="cover"
            zoomSpeed={1}
            zoomWithScroll
            showGrid
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
            <Button variant="contained" onClick={onApplyCrop} disabled={isLoading} sx={{ flex: 1 }}>
              Use this image
            </Button>
            <Fab size="small" onClick={onClose}><Close fontSize="small" /></Fab>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default AgentImage;
